/**
 * BOQ API Service
 * Bill of Quantities - project cost documents with catalog-linked entries
 *
 * API endpoints for:
 * - BOQ CRUD operations
 * - Section management
 * - Entry management (with catalog integration)
 * - Workflow (send, lock, approve)
 * - Change requests
 */

import type {
	BoqStarterUnpriced,
	BoqStarterUnpricedSummary,
} from "@shared/types";
import { apiClient } from "@/lib/api-client";

const API_BASE = "/api/v1";

// ============================================================================
// TYPES
// ============================================================================

export type BoqStatus =
	| "draft"
	| "sent"
	| "change_requested"
	| "approved"
	| "locked";
export type BoqRole = "designer" | "contractor";
export type ChangeRequestStatus = "open" | "approved" | "rejected";
export type StarterBoqTier = "standard" | "premium" | "luxury";
export type UoM =
	| "sq_ft"
	| "liter"
	| "piece"
	| "set"
	| "running_ft"
	| "kg"
	| "meter"
	| "nos"
	| "job";

export interface BOQ {
	id: string;
	/** Null for a standalone BOQ: a private estimate attached to no project. */
	projectId: string | null;
	versionNumber: number;
	previousBoqId?: string;
	createdByUserId: string;
	createdByRole: BoqRole;
	status: BoqStatus;
	title?: string;
	notes?: string;
	currencyCode: string;
	grandTotal: number;
	createdAt: string;
	updatedAt: string;
	sections?: BOQSection[];
	generationRun?: BOQGenerationRun;
}

export interface BOQSection {
	id: string;
	boqId: string;
	lineNo: number;
	category?: string;
	sectionName: string;
	description?: string;
	sectionTotal: number;
	isDefault: boolean;
	createdAt: string;
	updatedAt: string;
	entries?: BOQEntry[];
}

export interface BOQEntry {
	id: string;
	boqSectionId: string;
	lineNo: number;
	/** Null for a custom line, which carries its own `customName` instead. */
	catalogProductId: string | null;
	customName?: string | null;
	catalogVariantId?: string;
	roomReferenceId?: string;
	roomCustomName?: string;
	roomName?: string;
	description?: string;
	uom?: UoM;
	quantity: number;
	unitPrice: number;
	lineTotal: number;
	/**
	 * The GST rate (basis points) and the GST amount (paise) included in
	 * `lineTotal`. Null when the unit price is not a catalogue price (typed, a
	 * vendor quote, or one the catalogue has since moved off), and absent from
	 * an older response: either way shown as a dash, never as 0.
	 */
	gstRateBp?: number | null;
	gstAmount?: number | null;
	approvedByHomeowner: boolean;
	/** Set when the line was priced from a vendor quote. */
	sourceVendorId?: string | null;
	sourceVendorName?: string | null;
	createdAt: string;
	updatedAt: string;
	// Populated from catalog
	product?: {
		id: string;
		name: string;
		code?: string;
		imageUrl?: string;
		/** "man-day" in "per man-day"; null when the unit of measure is shown instead. */
		unitLabel?: string | null;
	};
	variant?: {
		id: string;
		sku: string;
	};
}

export interface BOQChangeRequest {
	id: string;
	boqId: string;
	requestedByUserId: string;
	message: string;
	status: ChangeRequestStatus;
	resolvedByUserId?: string;
	resolvedAt?: string;
	resolutionNote?: string;
	createdAt: string;
	updatedAt: string;
	requestedByUser?: {
		id: string;
		name: string;
		email?: string;
	};
	resolvedByUser?: {
		id: string;
		name: string;
	};
}

export interface BOQDetails {
	boq: BOQ;
	sections: Array<BOQSection & { entries: BOQEntry[] }>;
	generationRun?: BOQGenerationRun;
}

export interface BOQStarterTemplate {
	id: string;
	name: string;
	slug: string;
	description?: string | null;
	projectScope?: "full_home" | "partial" | null;
	supportedProviderRoles: BoqRole[];
	defaultTier: StarterBoqTier;
	isActive: boolean;
	sortOrder: number;
}

export interface BOQStarterAssumption {
	label: string;
	value: string;
	source: string;
}

export interface BOQStarterWarning {
	code: string;
	message: string;
	templateItemId?: string;
	catalogProductId?: string;
	catalogVariantId?: string | null;
	productName?: string;
	/** Why a `price_fallback` line has no price. */
	unpriced?: BoqStarterUnpriced;
}

export interface BOQStarterPreviewItem {
	templateItemId: string;
	catalogProductId: string;
	catalogVariantId?: string | null;
	description: string;
	uom?: UoM | null;
	quantity: number;
	unitPrice: number;
	lineTotal: number;
	/** GST included in `lineTotal`; null for a fixed template price. See BOQEntry. */
	gstRateBp?: number | null;
	gstAmount?: number | null;
	assumptions: string[];
	warnings: string[];
}

export interface BOQStarterPreviewSection {
	templateSectionId: string;
	sectionName: string;
	description?: string | null;
	lineNo: number;
	sectionTotal: number;
	items: BOQStarterPreviewItem[];
}

export interface BOQStarterPreview {
	template: BOQStarterTemplate;
	sections: BOQStarterPreviewSection[];
	assumptions: BOQStarterAssumption[];
	warnings: BOQStarterWarning[];
	estimatedTotal: number;
}

export interface BOQGenerationRun {
	id: string;
	boqId: string;
	templateId: string;
	createdBy: string;
	inputSnapshot: Record<string, unknown>;
	assumptions: BOQStarterAssumption[];
	warnings: BOQStarterWarning[];
	skippedItems: BOQStarterWarning[];
	createdAt: string;
	template?: {
		id: string;
		name: string;
		slug: string;
	};
}

// ============================================================================
// BOQ CRUD API
// ============================================================================

export async function createBOQ(
	projectId: string,
	data: { title?: string; notes?: string },
): Promise<{ success: boolean; boq?: BOQ; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/projects/${projectId}/boqs`,
			data,
			{ inlineErrors: true },
		);
		return { success: true, boq: response.data.data.boq };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create BOQ",
		};
	}
}

/** A BOQ that belongs to no project, private to the designer or contractor who made it. */
export async function createStandaloneBOQ(data: {
	title?: string;
	notes?: string;
}): Promise<{ success: boolean; boq?: BOQ; error?: string }> {
	try {
		const response = await apiClient.post(`${API_BASE}/boqs`, data, {
			inlineErrors: true,
		});
		return { success: true, boq: response.data.data.boq };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create BOQ",
		};
	}
}

export interface GlobalBOQItem extends BOQ {
	projectName?: string | null;
	projectCode?: string;
}

export async function getAllBOQs(params?: {
	status?: string;
}): Promise<{ data: GlobalBOQItem[]; error?: string }> {
	try {
		const response = await apiClient.get(`${API_BASE}/boqs`, {
			params: params?.status ? { status: params.status } : undefined,
		});
		return { data: response.data.data.boqs ?? [], error: undefined };
	} catch (error: unknown) {
		const err = error as {
			message?: string;
			response?: { status?: number; data?: { message?: string } };
		};
		return {
			data: [],
			error:
				err.response?.data?.message || err.message || "Failed to load BOQs",
		};
	}
}

export async function getProjectBOQs(
	projectId: string,
): Promise<{ data: BOQ[]; error?: string; forbidden?: boolean }> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/projects/${projectId}/boqs`,
		);
		return { data: response.data.data.boqs ?? [], error: undefined };
	} catch (error: unknown) {
		const err = error as {
			message?: string;
			response?: { status?: number; data?: { message?: string } };
		};
		return {
			data: [],
			error:
				err.response?.data?.message ||
				err.message ||
				"Failed to load project BOQs",
			forbidden: err.response?.status === 403,
		};
	}
}

export async function getBOQById(
	boqId: string,
): Promise<{ data: BOQDetails | null; error?: string }> {
	try {
		const response = await apiClient.get(`${API_BASE}/boqs/${boqId}`);
		return { data: response.data.data, error: undefined };
	} catch (error: unknown) {
		const err = error as {
			message?: string;
			response?: { data?: { message?: string } };
		};
		return {
			data: null,
			error:
				err.response?.data?.message ||
				err.message ||
				"Failed to load BOQ details",
		};
	}
}

// ============================================================================
// PRODUCT ALTERNATIVES API
// ============================================================================

export interface ProductAlternative {
	type: "value" | "premium" | "similar";
	product: {
		id: string;
		name: string;
		tier: string;
		price: number;
		priceDeltaPercent: number;
	};
}

export interface GetProductAlternativesResult {
	original: {
		id: string;
		name: string;
		tier: string;
		price: number;
	};
	alternatives: {
		value: ProductAlternative | null;
		premium: ProductAlternative | null;
		similar: ProductAlternative | null;
	};
}

export async function getProductAlternatives(
	catalogProductId: string,
): Promise<{ data: GetProductAlternativesResult | null; error?: string }> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/catalog/products/${catalogProductId}/alternatives`,
			{ inlineErrors: true },
		);
		return { data: response.data.data, error: undefined };
	} catch (error: unknown) {
		const err = error as {
			message?: string;
			response?: { data?: { message?: string } };
		};
		return {
			data: null,
			error:
				err.response?.data?.message ||
				err.message ||
				"Failed to load product alternatives",
		};
	}
}

// ============================================================================
// STARTER BOQ TEMPLATES API
// ============================================================================

export interface ListBOQStarterTemplatesResult {
	success: boolean;
	templates: BOQStarterTemplate[];
	nextCursor: string | null;
	error?: string;
}

export async function getBOQStarterTemplates(
	projectId: string,
	options: { limit?: number; cursor?: string } = {},
): Promise<ListBOQStarterTemplatesResult> {
	try {
		const params = new URLSearchParams();
		if (options.limit) params.set("limit", String(options.limit));
		if (options.cursor) params.set("cursor", options.cursor);
		const url = params.size
			? `${API_BASE}/projects/${projectId}/boq-starter-templates?${params}`
			: `${API_BASE}/projects/${projectId}/boq-starter-templates`;
		const response = await apiClient.get(url, { inlineErrors: true });
		return {
			success: true,
			templates: response.data.data.templates ?? [],
			nextCursor: response.data.data.nextCursor ?? null,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			templates: [],
			nextCursor: null,
			error: err.response?.data?.message || "Failed to load starter templates",
		};
	}
}

/**
 * Every active starter template the signed-in designer or contractor may use,
 * with no project in scope. A failure comes back as `success: false` so the
 * caller can tell it from a genuinely empty catalogue.
 */
export async function getBOQStarterTemplatesForUser(): Promise<ListBOQStarterTemplatesResult> {
	try {
		const response = await apiClient.get(`${API_BASE}/boq-starter-templates`, {
			inlineErrors: true,
		});
		return {
			success: true,
			templates: response.data.data.templates ?? [],
			nextCursor: null,
		};
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			templates: [],
			nextCursor: null,
			error: err.response?.data?.message || "Failed to load starter templates",
		};
	}
}

interface StarterTemplateRequestError {
	response?: { data?: { message?: string; code?: string; data?: unknown } };
}

/** What a starter-template request failure says beyond its message. */
export interface StarterFailureDetails {
	/** The server's `BoqStarterError` code, e.g. `malformed_rule`. */
	code?: string;
	/** For `template_unpriced`: lines per reason. */
	unpriced?: BoqStarterUnpricedSummary;
}

/**
 * The server's `BoqStarterError` code, and for `template_unpriced` its
 * reasons, so the form can explain a template it cannot use.
 */
function starterFailureDetails(
	err: StarterTemplateRequestError,
): StarterFailureDetails {
	const code = err.response?.data?.code;
	if (!code) return {};
	const data = err.response?.data?.data;
	if (
		code === "template_unpriced" &&
		data &&
		typeof data === "object" &&
		Array.isArray((data as { groups?: unknown }).groups)
	) {
		return { code, unpriced: data as BoqStarterUnpricedSummary };
	}
	return { code };
}

/** Starter-template endpoints take no project for a standalone BOQ. */
function starterTemplateBase(projectId: string | null): string {
	return projectId ? `${API_BASE}/projects/${projectId}` : API_BASE;
}

export async function previewBOQStarterTemplate(
	projectId: string | null,
	templateId: string,
): Promise<{
	success: boolean;
	preview?: BOQStarterPreview;
	error?: string;
	code?: string;
	unpriced?: BoqStarterUnpricedSummary;
}> {
	try {
		const response = await apiClient.post(
			`${starterTemplateBase(projectId)}/boq-starter-templates/${templateId}/preview`,
			{},
			// The form shows this failure inline.
			{ inlineErrors: true },
		);
		return { success: true, preview: response.data.data.preview };
	} catch (error: unknown) {
		const err = error as StarterTemplateRequestError;
		return {
			success: false,
			error: err.response?.data?.message || "Failed to preview starter BOQ",
			...starterFailureDetails(err),
		};
	}
}

export async function generateBOQFromStarterTemplate(
	projectId: string | null,
	templateId: string,
	data: {
		title?: string;
		notes?: string;
		idempotencyKey?: string;
	} = {},
): Promise<{
	success: boolean;
	boq?: BOQ;
	preview?: BOQStarterPreview;
	generationRun?: BOQGenerationRun;
	error?: string;
	code?: string;
	unpriced?: BoqStarterUnpricedSummary;
}> {
	try {
		const headers: Record<string, string> = {};
		if (data.idempotencyKey) {
			headers["Idempotency-Key"] = data.idempotencyKey;
		}
		const response = await apiClient.post(
			`${starterTemplateBase(projectId)}/boq-starter-templates/${templateId}/generate`,
			{ title: data.title, notes: data.notes },
			// The form shows this failure inline and in its own toast.
			{ headers, inlineErrors: true },
		);
		return {
			success: true,
			boq: response.data.data.boq,
			preview: response.data.data.preview,
			generationRun: response.data.data.generationRun,
		};
	} catch (error: unknown) {
		const err = error as StarterTemplateRequestError;
		return {
			success: false,
			error: err.response?.data?.message || "Failed to generate starter BOQ",
			...starterFailureDetails(err),
		};
	}
}

// ============================================================================
// SECTIONS API
// ============================================================================

export async function addBOQSection(
	boqId: string,
	data: {
		sectionName: string;
		category?: string;
		description?: string;
		isDefault?: boolean;
	},
): Promise<{ success: boolean; section?: BOQSection; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/sections`,
			data,
			{ inlineErrors: true },
		);
		return { success: true, section: response.data.data.section };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to add section",
		};
	}
}

export async function updateBOQSection(
	boqId: string,
	sectionId: string,
	data: { sectionName?: string; category?: string; description?: string },
): Promise<{ success: boolean; section?: BOQSection; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/boqs/${boqId}/sections/${sectionId}`,
			data,
			{ inlineErrors: true },
		);
		return { success: true, section: response.data.data.section };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update section",
		};
	}
}

export async function deleteBOQSection(
	boqId: string,
	sectionId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/boqs/${boqId}/sections/${sectionId}`, {
			inlineErrors: true,
		});
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete section",
		};
	}
}

// ============================================================================
// ENTRIES API
// ============================================================================

/**
 * A catalogue line names its product; a custom line names itself and its unit.
 * The server accepts exactly one of the two.
 */
export interface AddBOQEntryInput {
	catalogProductId?: string;
	customName?: string;
	uom?: UoM;
	catalogVariantId?: string;
	roomReferenceId?: string;
	roomCustomName?: string;
	description?: string;
	quantity: number;
	unitPrice: number;
}

/** What to call a BOQ line: its catalogue product, else its custom name. */
export function boqEntryName(
	entry: Pick<BOQEntry, "product" | "customName">,
): string {
	return entry.product?.name || entry.customName || "Unnamed item";
}

export async function addBOQEntry(
	boqId: string,
	sectionId: string,
	data: AddBOQEntryInput,
): Promise<{ success: boolean; entry?: BOQEntry; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/sections/${sectionId}/entries`,
			data,
			{ inlineErrors: true },
		);
		return { success: true, entry: response.data.data.entry };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to add entry",
		};
	}
}

/**
 * Add entry without specifying section - uses default "Ungrouped" section
 */
export async function addBOQEntryWithoutSection(
	boqId: string,
	data: AddBOQEntryInput,
): Promise<{ success: boolean; entry?: BOQEntry; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/entries`,
			data,
		);
		return { success: true, entry: response.data.data.entry };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to add entry",
		};
	}
}

export async function updateBOQEntry(
	boqId: string,
	sectionId: string,
	entryId: string,
	data: {
		catalogVariantId?: string;
		description?: string;
		quantity?: number;
		unitPrice?: number;
		roomReferenceId?: string;
		roomCustomName?: string;
	},
): Promise<{ success: boolean; entry?: BOQEntry; error?: string }> {
	try {
		const response = await apiClient.patch(
			`${API_BASE}/boqs/${boqId}/sections/${sectionId}/entries/${entryId}`,
			data,
			{ inlineErrors: true },
		);
		return { success: true, entry: response.data.data.entry };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to update entry",
		};
	}
}

export async function deleteBOQEntry(
	boqId: string,
	sectionId: string,
	entryId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(
			`${API_BASE}/boqs/${boqId}/sections/${sectionId}/entries/${entryId}`,
			{ inlineErrors: true },
		);
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete entry",
		};
	}
}

export async function homeownerApproveBoqEntry(
	boqId: string,
	sectionId: string,
	entryId: string,
): Promise<{ success: boolean; entry?: BOQEntry; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/sections/${sectionId}/entries/${entryId}/approve`,
			undefined,
			{ inlineErrors: true },
		);
		return { success: true, entry: response.data.data.entry };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to approve BOQ entry",
		};
	}
}

export async function homeownerBulkApproveBoqEntries(
	boqId: string,
	entryIds: string[],
): Promise<{ success: boolean; entries?: BOQEntry[]; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/approve-entries`,
			{ entryIds },
			{ inlineErrors: true },
		);
		return { success: true, entries: response.data.data.entries };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to approve BOQ entries",
		};
	}
}

export async function deleteBOQ(
	boqId: string,
): Promise<{ success: boolean; error?: string }> {
	try {
		await apiClient.delete(`${API_BASE}/boqs/${boqId}`, { inlineErrors: true });
		return { success: true };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to delete BOQ",
		};
	}
}

// ============================================================================
// WORKFLOW API
// ============================================================================

export async function sendBOQ(
	boqId: string,
): Promise<{ success: boolean; boq?: BOQ; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/send`,
			undefined,
			{ inlineErrors: true },
		);
		return { success: true, boq: response.data.data.boq };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to send BOQ",
		};
	}
}

export async function reopenBOQ(
	boqId: string,
): Promise<{ success: boolean; boq?: BOQ; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/reopen`,
			undefined,
			{ inlineErrors: true },
		);
		return { success: true, boq: response.data.data.boq };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to reopen BOQ",
		};
	}
}

export async function lockBOQ(
	boqId: string,
): Promise<{ success: boolean; boq?: BOQ; error?: string }> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/lock`,
			undefined,
			{ inlineErrors: true },
		);
		return { success: true, boq: response.data.data.boq };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to lock BOQ",
		};
	}
}

// ============================================================================
// CHANGE REQUESTS API
// ============================================================================

export async function getBOQChangeRequests(
	boqId: string,
): Promise<{ data: BOQChangeRequest[]; error?: string }> {
	try {
		const response = await apiClient.get(
			`${API_BASE}/boqs/${boqId}/change-requests`,
		);
		return { data: response.data.data.changeRequests ?? [], error: undefined };
	} catch (error: unknown) {
		const err = error as {
			message?: string;
			response?: { data?: { message?: string } };
		};
		return {
			data: [],
			error:
				err.response?.data?.message ||
				err.message ||
				"Failed to load change requests",
		};
	}
}

export async function createBOQChangeRequest(
	boqId: string,
	message: string,
): Promise<{
	success: boolean;
	changeRequest?: BOQChangeRequest;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boqs/${boqId}/change-requests`,
			{
				message,
			},
			{ inlineErrors: true },
		);
		return { success: true, changeRequest: response.data.data.changeRequest };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to create change request",
		};
	}
}

export async function approveBOQChangeRequest(
	changeRequestId: string,
	resolutionNote?: string,
): Promise<{
	success: boolean;
	changeRequest?: BOQChangeRequest;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boq-change-requests/${changeRequestId}/approve`,
			{
				resolutionNote,
			},
			{ inlineErrors: true },
		);
		return { success: true, changeRequest: response.data.data.changeRequest };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to approve change request",
		};
	}
}

export async function rejectBOQChangeRequest(
	changeRequestId: string,
	resolutionNote?: string,
): Promise<{
	success: boolean;
	changeRequest?: BOQChangeRequest;
	error?: string;
}> {
	try {
		const response = await apiClient.post(
			`${API_BASE}/boq-change-requests/${changeRequestId}/reject`,
			{
				resolutionNote,
			},
			{ inlineErrors: true },
		);
		return { success: true, changeRequest: response.data.data.changeRequest };
	} catch (error: unknown) {
		const err = error as { response?: { data?: { message?: string } } };
		return {
			success: false,
			error: err.response?.data?.message || "Failed to reject change request",
		};
	}
}

// ============================================================================
// UTILITIES
// ============================================================================

/**
 * Format amount in paise to currency string
 */
export function formatCurrency(
	amountInPaise: number,
	currencyCode: string = "INR",
): string {
	return new Intl.NumberFormat("en-IN", {
		style: "currency",
		currency: currencyCode,
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	}).format(amountInPaise / 100);
}

/**
 * Format amount in paise to plain number string
 */
export function formatAmount(amountInPaise: number): string {
	return (amountInPaise / 100).toFixed(2);
}

/**
 * Get status badge color class
 */
export function getStatusColor(
	status: BoqStatus | ChangeRequestStatus,
): string {
	const colorMap: Record<BoqStatus | ChangeRequestStatus, string> = {
		draft: "bg-dashboard-pill-neutral-bg text-dashboard-ink-soft",
		sent: "bg-dashboard-pill-info-bg text-dashboard-info-text",
		change_requested: "bg-dashboard-pill-awaiting-bg text-dashboard-warning",
		approved: "bg-dashboard-pill-done-bg text-dashboard-success-strong",
		locked:
			"bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200",
		open: "bg-dashboard-pill-awaiting-bg text-dashboard-warning",
		rejected: "bg-dashboard-pill-risk-bg text-dashboard-danger-strong",
	};
	return (
		colorMap[status] || "bg-dashboard-pill-neutral-bg text-dashboard-ink-soft"
	);
}

/**
 * Get display label for status
 */
export function getStatusLabel(
	status: BoqStatus | ChangeRequestStatus,
): string {
	return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
}

/**
 * Check if BOQ is editable based on status and user role
 */
export function isBOQEditable(
	boq: BOQ,
	userRole: "homeowner" | "designer" | "contractor",
): boolean {
	// Homeowners can never edit BOQ entries directly
	if (userRole === "homeowner") return false;

	// Designers and contractors can only edit draft or change_requested BOQs
	return boq.status === "draft" || boq.status === "change_requested";
}

/**
 * Check if change requests can be created
 */
export function canCreateChangeRequest(
	boq: BOQ,
	userRole: "homeowner" | "designer" | "contractor",
): boolean {
	// Only homeowners can create change requests
	if (userRole !== "homeowner") return false;

	// Can only request changes on sent or approved BOQs
	return (
		boq.status === "sent" ||
		boq.status === "change_requested" ||
		boq.status === "approved"
	);
}

/**
 * Check if BOQ can be sent
 */
export function canSendBOQ(boq: BOQ): boolean {
	return boq.status === "draft" || boq.status === "change_requested";
}

/**
 * Check if a sent BOQ can be taken back to draft for editing. Only its author's
 * side can, and only while it is Sent: an approved or locked BOQ is final.
 */
export function canReopenBOQ(
	boq: BOQ,
	userRole: "homeowner" | "designer" | "contractor",
): boolean {
	return userRole !== "homeowner" && boq.status === "sent";
}

/**
 * Check if BOQ can be locked
 */
export function canLockBOQ(boq: BOQ): boolean {
	return boq.status === "sent" || boq.status === "approved";
}

// ============================================================================
// CUSTOM TEMPLATES
// ============================================================================

export interface CustomTemplateItem {
	id: string;
	description: string;
	uom: UoM;
	quantity: number;
	unitPrice: number;
	lineTotal: number;
	catalogProductId?: string;
}

export interface CustomTemplateSection {
	id: string;
	name: string;
	description?: string;
	sortOrder: number;
	items: CustomTemplateItem[];
}

export interface CustomBOQTemplate {
	id: string;
	name: string;
	slug: string;
	description?: string;
	projectScope?: "full_home" | "partial";
	defaultTier: StarterBoqTier;
	supportedProviderRoles: BoqRole[];
	isActive: boolean;
	isSystem: boolean;
	sections: CustomTemplateSection[];
	createdAt: string;
	updatedAt: string;
}

const CUSTOM_TEMPLATES_STORAGE_KEY = "finishd_custom_boq_templates";

const DEFAULT_CUSTOM_TEMPLATES: CustomBOQTemplate[] = [
	{
		id: "tmpl-custom-1",
		name: "Signature 2BHK Interior Package",
		slug: "signature-2bhk-interior-package",
		description:
			"Complete woodwork, false ceiling, and modular kitchen specifications for standard 2BHK apartments.",
		projectScope: "full_home",
		defaultTier: "premium",
		supportedProviderRoles: ["designer", "contractor"],
		isActive: true,
		isSystem: false,
		sections: [
			{
				id: "sec-1",
				name: "Living & Dining Room",
				sortOrder: 1,
				items: [
					{
						id: "item-1",
						description: "Gypsum False Ceiling with Cove Lighting Profile",
						uom: "sq_ft",
						quantity: 320,
						unitPrice: 14500, // 145 INR in paisa
						lineTotal: 4640000,
					},
					{
						id: "item-2",
						description: "TV Console Unit in High Gloss Acrylic Finish",
						uom: "running_ft",
						quantity: 8,
						unitPrice: 220000, // 2200 INR in paisa
						lineTotal: 1760000,
					},
				],
			},
			{
				id: "sec-2",
				name: "Modular Kitchen",
				sortOrder: 2,
				items: [
					{
						id: "item-3",
						description:
							"Base & Overhead Cabinets in BWR Marine Ply (Laminate)",
						uom: "running_ft",
						quantity: 18,
						unitPrice: 285000, // 2850 INR in paisa
						lineTotal: 5130000,
					},
					{
						id: "item-4",
						description: "Soft Close Tandem Box Drawers with Cutlery Trays",
						uom: "set",
						quantity: 4,
						unitPrice: 450000, // 4500 INR in paisa
						lineTotal: 1800000,
					},
				],
			},
		],
		createdAt: "2026-10-01T10:00:00.000Z",
		updatedAt: "2026-10-01T10:00:00.000Z",
	},
];

function readStoredTemplates(): CustomBOQTemplate[] {
	if (typeof window === "undefined" || !window.localStorage) {
		return DEFAULT_CUSTOM_TEMPLATES;
	}
	try {
		const raw = window.localStorage.getItem(CUSTOM_TEMPLATES_STORAGE_KEY);
		if (!raw) {
			window.localStorage.setItem(
				CUSTOM_TEMPLATES_STORAGE_KEY,
				JSON.stringify(DEFAULT_CUSTOM_TEMPLATES),
			);
			return DEFAULT_CUSTOM_TEMPLATES;
		}
		return JSON.parse(raw);
	} catch {
		return DEFAULT_CUSTOM_TEMPLATES;
	}
}

function writeStoredTemplates(templates: CustomBOQTemplate[]): void {
	if (typeof window === "undefined" || !window.localStorage) return;
	try {
		window.localStorage.setItem(
			CUSTOM_TEMPLATES_STORAGE_KEY,
			JSON.stringify(templates),
		);
	} catch (e) {
		console.warn("Failed to write custom BOQ templates to storage", e);
	}
}

export async function getCustomTemplates(): Promise<CustomBOQTemplate[]> {
	return readStoredTemplates();
}

export async function saveCustomTemplate(
	templateData: Omit<CustomBOQTemplate, "id" | "createdAt" | "updatedAt"> & {
		id?: string;
	},
): Promise<CustomBOQTemplate> {
	const current = readStoredTemplates();
	const now = new Date().toISOString();

	if (templateData.id) {
		const idx = current.findIndex((t) => t.id === templateData.id);
		if (idx >= 0) {
			const updated: CustomBOQTemplate = {
				...current[idx],
				...templateData,
				id: templateData.id,
				updatedAt: now,
			};
			current[idx] = updated;
			writeStoredTemplates(current);
			return updated;
		}
	}

	const newTemplate: CustomBOQTemplate = {
		...templateData,
		id: `tmpl-custom-${Date.now()}`,
		slug:
			templateData.slug ||
			templateData.name
				.toLowerCase()
				.replace(/[^a-z0-9]+/g, "-")
				.replace(/^-|-$/g, ""),
		isSystem: false,
		createdAt: now,
		updatedAt: now,
	};
	current.unshift(newTemplate);
	writeStoredTemplates(current);
	return newTemplate;
}

export async function duplicateCustomTemplate(
	templateId: string,
): Promise<CustomBOQTemplate> {
	const current = readStoredTemplates();
	const original = current.find((t) => t.id === templateId);
	if (!original) {
		throw new Error("Template not found");
	}

	const now = new Date().toISOString();
	const duplicated: CustomBOQTemplate = {
		...original,
		id: `tmpl-custom-${Date.now()}`,
		name: `${original.name} (Copy)`,
		slug: `${original.slug}-copy-${Date.now().toString().slice(-4)}`,
		isSystem: false,
		createdAt: now,
		updatedAt: now,
		sections: original.sections.map((sec, sIdx) => ({
			...sec,
			id: `sec-dup-${sIdx}-${Date.now()}`,
			items: sec.items.map((it, iIdx) => ({
				...it,
				id: `item-dup-${iIdx}-${Date.now()}`,
			})),
		})),
	};

	current.unshift(duplicated);
	writeStoredTemplates(current);
	return duplicated;
}

export async function deleteCustomTemplate(templateId: string): Promise<void> {
	const current = readStoredTemplates();
	const filtered = current.filter((t) => t.id !== templateId);
	writeStoredTemplates(filtered);
}

export async function saveBoqAsTemplate(
	boq: BOQ,
	name: string,
	description?: string,
	projectScope?: "full_home" | "partial",
	defaultTier?: StarterBoqTier,
): Promise<CustomBOQTemplate> {
	const sections: CustomTemplateSection[] = (boq.sections || []).map(
		(sec, sIdx) => ({
			id: `sec-${Date.now()}-${sIdx}`,
			name: sec.sectionName,
			description: sec.description,
			sortOrder: sec.lineNo || sIdx + 1,
			items: (sec.entries || []).map((entry, eIdx) => ({
				id: `item-${Date.now()}-${sIdx}-${eIdx}`,
				description:
					entry.description ||
					entry.product?.name ||
					entry.customName ||
					`Item ${entry.lineNo || eIdx + 1}`,
				uom: (entry.uom as UoM) || "piece",
				quantity: entry.quantity,
				unitPrice: entry.unitPrice,
				lineTotal: entry.lineTotal,
				catalogProductId: entry.catalogProductId ?? undefined,
			})),
		}),
	);

	return saveCustomTemplate({
		name,
		slug: name
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, ""),
		description: description || `Created from BOQ #${boq.versionNumber}`,
		projectScope: projectScope || "full_home",
		defaultTier: defaultTier || "standard",
		supportedProviderRoles: [boq.createdByRole || "designer"],
		isActive: true,
		isSystem: false,
		sections,
	});
}

export function customTemplateToPreview(
	template: CustomBOQTemplate,
): BOQStarterPreview {
	const sections: BOQStarterPreviewSection[] = template.sections.map(
		(sec, idx) => {
			const items: BOQStarterPreviewItem[] = sec.items.map((it) => ({
				templateItemId: it.id,
				catalogProductId: it.catalogProductId || "custom-product",
				catalogVariantId: null,
				description: it.description,
				uom: it.uom,
				quantity: it.quantity,
				unitPrice: it.unitPrice,
				lineTotal: it.lineTotal,
				assumptions: [],
				warnings: [],
			}));

			const sectionTotal = items.reduce((sum, item) => sum + item.lineTotal, 0);

			return {
				templateSectionId: sec.id,
				sectionName: sec.name,
				description: sec.description || null,
				lineNo: sec.sortOrder || idx + 1,
				sectionTotal,
				items,
			};
		},
	);

	const estimatedTotal = sections.reduce(
		(sum, sec) => sum + sec.sectionTotal,
		0,
	);

	return {
		template: {
			id: template.id,
			name: template.name,
			slug: template.slug,
			description: template.description || null,
			projectScope: template.projectScope || null,
			supportedProviderRoles: template.supportedProviderRoles,
			defaultTier: template.defaultTier,
			isActive: template.isActive,
			sortOrder: 0,
		},
		estimatedTotal,
		sections,
		assumptions: [],
		warnings: [],
	};
}
